import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Loader2, Package, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, History, PackageX,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQty = (n) => Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const fmtTanggal = (t) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(t || ""));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(t);
  return isNaN(d) ? "-" : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export default function PublicBincard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [fotoSrc, setFotoSrc] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/bincard/${id}`)
      .then((res) => {
        setData(res.data);
        if (res.data.barang?.has_foto) {
          axios.get(`${API}/public/bincard/${id}/foto`, { responseType: "blob" })
            .then((r) => setFotoSrc(URL.createObjectURL(r.data)))
            .catch(() => {});
        }
      })
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="public-bincard-error">
        <div className="text-center space-y-3">
          <PackageX className="h-10 w-10 mx-auto text-slate-300" />
          <p className="text-slate-500 text-sm">Barang tidak ditemukan atau link tidak valid.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="public-bincard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const b = data.barang;
  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="public-bincard-page">
      <div className="bg-blue-700 text-white px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <Package className="h-5 w-5" />
          <div>
            <p className="text-sm font-bold leading-tight">Bin Card — Info Stok Barang</p>
            <p className="text-[11px] text-blue-200">PATRA Facility Management · tampilan publik (read-only)</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 mt-4 space-y-4">
        <Card className="p-4 border-slate-200 shadow-sm rounded-xl" data-testid="public-bincard-info">
          <div className="flex gap-3.5">
            {fotoSrc ? (
              <img src={fotoSrc} alt={b.nama} data-testid="public-bincard-foto"
                className="h-20 w-20 rounded-lg object-cover border border-slate-200 shrink-0" />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center shrink-0">
                <Package className="h-8 w-8" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-mono text-slate-500" data-testid="public-bincard-kode">{b.kode}</p>
              <p className="text-base font-bold text-slate-900 leading-snug" data-testid="public-bincard-nama">{b.nama}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {b.jenis || "-"}{b.satuan ? ` · Satuan: ${b.satuan}` : ""}
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-2.5 text-center col-span-3">
              <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide">Stok Terkini</p>
              <p className="text-3xl font-extrabold text-blue-900" data-testid="public-bincard-stok">
                {fmtQty(b.stok_akhir)}<span className="text-sm font-semibold text-blue-500 ml-1">{b.satuan || ""}</span>
              </p>
              {b.menipis && (
                <Badge className="mt-1 bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1" data-testid="public-bincard-menipis">
                  <AlertTriangle className="h-3 w-3" /> Stok menipis
                </Badge>
              )}
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
              <p className="text-[10px] text-slate-500">Masuk</p>
              <p className="text-sm font-bold text-emerald-600">{fmtQty(b.masuk)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
              <p className="text-[10px] text-slate-500">Keluar</p>
              <p className="text-sm font-bold text-rose-600">{fmtQty(b.keluar)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
              <p className="text-[10px] text-slate-500">Stok Min</p>
              <p className="text-sm font-bold text-slate-700">{fmtQty(b.stok_minimum)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-slate-200 shadow-sm rounded-xl" data-testid="public-bincard-riwayat">
          <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-3">
            <History className="h-4 w-4 text-blue-600" /> Riwayat Transaksi
          </p>
          {data.riwayat.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center" data-testid="public-bincard-riwayat-empty">
              Belum ada transaksi keluar/masuk untuk barang ini.
            </p>
          ) : (
            <div className="space-y-2">
              {data.riwayat.map((t) => (
                <div key={t.id} className="flex items-start gap-2.5 rounded-lg bg-slate-50/70 border border-slate-100 p-2.5"
                  data-testid={`public-riwayat-item-${t.id}`}>
                  <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${
                    t.tipe === "MASUK" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                    {t.tipe === "MASUK" ? <ArrowDownToLine className="h-3.5 w-3.5" /> : <ArrowUpFromLine className="h-3.5 w-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-bold ${t.tipe === "MASUK" ? "text-emerald-700" : "text-rose-700"}`}>
                        {t.tipe === "MASUK" ? "Masuk" : "Keluar"} {fmtQty(t.qty)}{t.satuan ? ` ${t.satuan}` : ""}
                      </p>
                      {t.opname && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">Opname</Badge>
                      )}
                      <span className="text-[10px] text-slate-400 ml-auto shrink-0">{fmtTanggal(t.tanggal)}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      {t.tipe === "MASUK"
                        ? <>Pengirim: <span className="font-medium">{t.pengirim || "-"}</span></>
                        : <>Pengambil: <span className="font-medium">{t.pengambil || "-"}</span>
                            {t.lokasi_area ? ` · ${t.lokasi_area}` : ""}</>}
                    </p>
                    {t.keterangan && <p className="text-[11px] text-slate-400 mt-0.5">{t.keterangan}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <p className="text-[11px] text-slate-400 text-center">
          Data diperbarui otomatis setiap halaman ini dibuka · Hanya untuk dilihat, tidak dapat diubah.
        </p>
      </div>
    </div>
  );
}
